import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { processAiProduct, updateAiProduct } from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import Backdrop from '@shared/components/backdrop';
import CircularProgress from '@shared/components/circular-progress';
import TextField from './TextField';
import LangTextField from './LangTextField';
import CreateIcon from '@resources/icons/CreateIcon';
import CheckIcon from '@resources/icons/CheckIcon';
import EditIcon from '@resources/icons/EditIcon';
import LimitBanner from '@shared/components/page-parts/LimitBanner';
import AIProduct from '..';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    gap: '20px',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '20px',
    border: '1px solid #00C98F',
    borderRadius: '4px',
  },
  uploadImage: {
    width: '176px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #E3E9F1',
    flex: '0 0 176px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '95%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    marginTop: '50px',
    gap: '16px',
    '& > button': {
      minWidth: '142px',
      width: '100%',
    },
    '& > button:first-child': {
      borderColor: '#192A3E',
      color: '#192A3E',
    },
  },
  toolBarWrapper: {
    display: 'flex',
    gap: '20px',
    width: '100%',
    '& > button': {
      minWidth: '200px',
      height: '42px',
      width: '100%',
    },
  },
  buttonCreate: {
    fontSize: '13px',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
      '& > path': {
        stroke: '#fff',
      },
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  chooseImages: {
    maxWidth: '172px',
    maxHeight: '42px',
    width: '100%',
    height: '100%',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '5px',
    justifyContent: 'center',
    border: '1px solid #C3D0E1',
    borderRadius: '4px',
    color: '#192A3E',
    '&:after': {
      content: "''",
      display: 'block',
      width: 0,
      height: 0,
      borderLeft: '.5rem solid transparent',
      borderRight: '.5rem solid transparent',
      borderTop: '.5rem solid #192A3E',
      marginLeft: '.4rem',
    },
    '&.active': {
      borderColor: '#00C98F',
      color: '#00C98F',
      '&:after': {
        transform: 'rotate(180deg)',
        borderTopColor: '#00C98F',
      },
      '& > svg path': {
        fill: '#00C98F',
      },
    },
    '&.selected': {
      borderColor: '#00C98F',
      color: '#00C98F',
      '&:after': {
        borderTopColor: '#00C98F',
      },
      '& > svg path': {
        fill: '#00C98F',
      },
    },
  },
  chooseImageEmpty: {
    objectFit: 'cover',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '268px',
    height: '184px',
    border: '1px solid #E3E9F1',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  uploadVideo: {
    width: '268px',
    position: 'relative',
    display: 'flex',
    height: '184px',
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  chooseImageEmptyText: {
    fontSize: '13px',
    fontWeight: 400,
  },
  formDialog: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  colorButton: {
    position: 'relative',
    width: '160px',
  },
  colorPicker: {
    position: 'absolute',
    zIndex: 1,
  },
  backdrop: {
    top: 0,
    borderRadius: '4px',
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      position: 'absolute',
      filter: 'opacity(0.5)',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > span': {
      position: 'absolute',
    },
  },
  iconsWrapper: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
    zIndex: 2,
  },
  iconButton: {
    cursor: 'pointer',
    display: 'flex',
    border: '1px solid #E3E9F1',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: '50%',
    padding: '4px',
    '& > svg': {
      stroke: '#9DA3B2',
    },
    '&.active': {
      borderColor: theme.palette.primary.main,
      '& > svg': {
        stroke: theme.palette.primary.main,
      },
    },
  },
  createIcon: {
    '& > svg path': {
      fill: '#9DA3B2',
    },
  },
  selectIcon: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: '4px',
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    border: 'none',
    zIndex: 2,
    background: '#E3E9F1',
    '& > svg path': {
      fill: '#fff',
      stroke: 'inherit',
    },
    '&.active': {
      background: theme.palette.primary.main,
    },
  },
  descriptionWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    gap: '20px',

  },
  descriptionItem: {
    flex: '0 0 418px',
    maxHeight: '320px',
    height: '100%',
    width: '418px',
    position: 'relative',
    marginBottom: '15px',
  },
  descriptionInput: {
    height: '100%',
    '& > .MuiInputBase-root': {
      height: '100%',
    },
    '& > .MuiInputBase-root textarea': {
      height: '100%',
      maxHeight: '260px',
      overflowY: 'auto !important',
      color: '#272D4E',
      padding: '0 15px',
      margin: '25px 0',
    },
    '&.generating': {
      filter: 'blur(5px)',
    },
  },
}));

export function TextStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const product = useSelector(selectAiProduct);
  const [texts, setTexts] = useState([]);
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingText, setEditingText] = useState(null);
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  useEffect(() => {
    setTexts(product.texts ?? []);
  }, [product]);

  const handleTextChange = (textId, newText) => {
    setEditingText(newText);
  };

  const handleCheckClick = (textId) => {
    const updatedTexts = product.texts.map((text) => ({
      ...text,
      selected: text.id === textId ? !text.selected : false,
    }));

    setTexts(updatedTexts);
    dispatch(
      updateAiProduct({
        ...product,
        texts: updatedTexts,
      }),
    );
  };


  const handleEditClick = (textId) => {
    if (editingTextId === textId) {
      const updatedTexts = product.texts.map(text => text.id === textId ? {...text, text: editingText} : text);
      dispatch(updateAiProduct({
        ...product,
        texts: updatedTexts,
      }));
      setTexts(updatedTexts);
      setEditingTextId(null);
      setEditingText(null);
    } else {
      setEditingTextId(textId);
      setEditingText(product.texts?.find((t) => t.id === textId)?.text || '');
    }
  };

  const handleCreateAll = async () => {
    const updatingTexts = product.texts.map(text => ({
      ...text,
      state: 'TEXT_GENERATION',
    }));
    setTexts(updatingTexts);
    dispatch(updateAiProduct({
      ...product,
      texts: updatingTexts,
    }));
    const allTextIds = product.texts.map(text => ({ textId: text.id }));
    dispatch(processAiProduct(product.id, {
      tasks: ['TEXT_REGENERATE'],
      texts: allTextIds,
      isBeta: false,
    }));
  };

  const handleRecreate = async (textId) => {
    const updatingTexts = product.texts.map(text => text.id === textId ? {...text, state: 'TEXT_GENERATION'} : text);
    setTexts(updatingTexts);
    setEditingTextId(null);
    setEditingText(null);
    dispatch(updateAiProduct({
      ...product,
      texts: updatingTexts,
    }));
    dispatch(processAiProduct(product.id, {
      tasks: ['TEXT_REGENERATE'],
      texts: [{textId}],
      isBeta: false,
    }));
  };

  const hasTextGenerationProcess = texts.some((text) => text.state === 'TEXT_GENERATION');

  const isNextDisabled = texts.every((txt) => !txt.selected) ?? true;

  return (
    <div className={ classes.pageWrapper }>
      <AIProduct />
      <div className={ classes.header }>
        Product Detail Page Content Pack
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
          Step 3. Product Description
          <div className={ classes.contentSubHeader }>
            Select the description option that you like. You can fix it manually or regenerate it.
          </div>
        </div>
        <div className={ classes.toolBarWrapper }>
          <div className={ classes.autocomplete }>
            <LangTextField
              value={ product.language }
              onChange={ (language) => {
                dispatch(updateAiProduct({
                  ...product,
                  language,
                }));
              } }
            />
          </div>
          <Button
            disabled={ !product.language || hasTextGenerationProcess || isAiProductReadOnly }
            onClick={ handleCreateAll }
          >
            <div className={ classes.buttonCreate }>
              <CreateIcon />
              Create
            </div>
          </Button>
        </div>
        <div className={ classes.descriptionWrapper }>
          {!texts.length && (
            <Backdrop className={ classes.backdrop }>
            <CircularProgress size="large" />
            Text Generation...
          </Backdrop>
          )}
          {texts.map((text) => (
            <div key={ text.id } className={ classes.descriptionItem }>
              {text.state === 'TEXT_GENERATION' && (
                <Backdrop className={ classes.backdrop }>
                  <CircularProgress size="large" />
                  Text Generation...
                </Backdrop>
              )}
              {text.state === 'TEXT_GENERATION_FINISHED' && (
              <div className={ classes.iconsWrapper }>
                <div
                  className={ classNames(classes.iconButton, {
                    active: editingTextId === text.id,
                  }) }
                  onClick={ () => handleEditClick(text.id) }
                >
                  <EditIcon />
                </div>
                { !isAiProductReadOnly && (
                <div
                  className={ classNames(classes.iconButton, classes.createIcon) }
                  onClick={ () => handleRecreate(text.id) }
                >
                  <CreateIcon />
                </div>
                )}
              </div>
              )}
              <TextField
                value={ text.id == editingTextId ? editingText : text.text }
                onChange={ (e) => handleTextChange(text.id, e.target.value) }
                className={ classNames(classes.descriptionInput, {
                  generating: text.state === 'TEXT_GENERATION',
                }) }
                disabled={ editingTextId !== text.id }
                multiline
                minRows={ 6 }
                required
              />
              {text.state === 'TEXT_GENERATION_FINISHED' && (
              <div
                className={ classNames(classes.selectIcon, {
                  active: text.selected,
                }) }
                onClick={ () => handleCheckClick(text.id) }
              >
                <CheckIcon size="small" />
              </div>
              )}
            </div>
          ))}
        </div>
        <div className={ classes.actions }>
          <Button
            color="secondary"
            variant="outlined"
            onClick={ () => {
              goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.BACKGROUND(product.id)}`);
            } }
          >
            Back
          </Button>
          <Button
            disabled={ isNextDisabled }
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.CAPTION(product.id)}`);
              }));
            } }
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
